import { AuthorizationDocument } from '../../Authorization.graphql';
import { GqlAuthorizationGroup } from '../../../../graphql/base-types.graphql';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans } from '@lingui/react';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { Radio } from '@startuptools/ui/Radio';
import { Alert } from '@startuptools/ui/joy/Alert';
import { Link } from '@startuptools/ui/Link';
import { useAgSetNoBoardStatementMutation } from '../../../../graphql/react-operations';
import { captureException } from '@sentry/react';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../components/steppers/common/StepperStepContentContext';
import { ErrorDisplay } from '../../../../components/error-display/ErrorDisplay';

type FormValues = {
  noBoardStatement: 'true' | 'false';
};

export const AgStepBoardStatement = ({
  authorizationGroup,
}: {
  authorizationGroup: Pick<GqlAuthorizationGroup, 'id' | 'noBoardStatement' | 'companyId'>;
}) => {
  const { control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: { noBoardStatement: authorizationGroup.noBoardStatement ? 'true' : 'false' },
  });
  const [doSubmit, { loading }] = useAgSetNoBoardStatementMutation({
    refetchQueries: [AuthorizationDocument],
    awaitRefetchQueries: true,
  });
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      await doSubmit({
        variables: {
          companyId: authorizationGroup.companyId,
          input: { id: authorizationGroup.id, noBoardStatement: values.noBoardStatement === 'true' },
        },
      });
      return stepNext();
    } catch (e) {
      captureException(e);
      genericErrorToast();
    }
    return;
  };

  const noBoardStatement = watch('noBoardStatement');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Board Statement" />
        </Typography>

        <Alert
          expandContent={
            <>
              <p>
                <Trans id="Producing the accounts also takes time and it comes with a cost to let the auditor review these. If you do not have an auditor you need to contract an external auditor solely for this purpose." />
              </p>
              <p>
                <Trans id="One can avoid most of these accounts by including in the decision of the authorization that the shareholders tolerate that such accounts does not need to be given. We recommend that you include this in the authorization. Though, to be valid, this needs to be approved by all shareholders, not just the ones who were present at the shareholder meeting. Unless all shareholders are present, and vote in favor of the proposal, you still need to produce these accounts." />
              </p>
              <p>
                <Trans
                  id="If the payment is settled in-kind an auditor needs to give an account of the payment (<link>company legislation, 13 chapter § 23</link>). This is the equivalent of the bank note from a cash settlement. Such an account must always be produced if payment it settled in-kind, even if you have a decision on abstaining from the others."
                  components={{
                    link: (
                      <Link
                        href="https://www.riksdagen.se/sv/dokument-lagar/dokument/svensk-forfattningssamling/aktiebolagslag-2005551_sfs-2005-551#K13P23"
                        target="_blank"
                      />
                    ),
                  }}
                />
              </p>
            </>
          }
        >
          <Trans
            id="When a company decides to issue shares, warrants or convertibles with an authorization, one or more account needs to be stated: One for special events since the shareholder meeting and one for each set-off and in-kind transaction, if you use set-off or in-kind transactions. The documents effected are stated in the company legislation <linkChap13>chapter 13, §§ 6-8</linkChap13>, <linkChap14>chapter 14, §§ 8-10</linkChap14> and <linkChap15>chapter 15, §§ 8-10</linkChap15>"
            components={{
              linkChap13: (
                <Link
                  href="https://www.riksdagen.se/sv/dokument-lagar/dokument/svensk-forfattningssamling/aktiebolagslag-2005551_sfs-2005-551#K13P6"
                  target="_blank"
                />
              ),
              linkChap14: (
                <Link
                  href="https://www.riksdagen.se/sv/dokument-lagar/dokument/svensk-forfattningssamling/aktiebolagslag-2005551_sfs-2005-551#K14P8"
                  target="_blank"
                />
              ),
              linkChap15: (
                <Link
                  href="https://www.riksdagen.se/sv/dokument-lagar/dokument/svensk-forfattningssamling/aktiebolagslag-2005551_sfs-2005-551#K15P8"
                  target="_blank"
                />
              ),
            }}
          />
        </Alert>

        <Controller
          control={control}
          name="noBoardStatement"
          render={({ field }) => (
            <RadioGroup {...field} orientation="vertical">
              <Radio
                value="true"
                label={
                  <Trans id='The shareholders approve that the company issues shares without creating certain documents according to the Swedish Companies Act ("Aktiebolagslagen") chapter 13 §§ 6-8, chapter 14, §§ 8-10 and chapter 15, §§ 8-10' />
                }
              />
              <Radio
                value="false"
                label={
                  <Trans id="The company must create such documents as specified the Swedish Companies Act, chapters 13 §§ 6-8, chapter 14 §§ 8-10 and chapter 15, §§ 8-10" />
                }
              />
            </RadioGroup>
          )}
        />

        {noBoardStatement === 'true' && (
          <Alert>
            <Trans
              id="<header>Attention!</header> All shareholders must be represented at the shareholder meeting, either in person or via power of attorney."
              components={{ header: <Typography level="h4" /> }}
            />
          </Alert>
        )}

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
